/**
 * OCHAT: the single list of sections reserved for the platform super admin.
 *
 * ONE LIST, THREE CONSUMERS. They cannot drift:
 *   - ochat/sidebar/menuPolicy.js         hides the entries from the sidebar
 *   - ochat/routes/routePermissionPolicy.js blocks the URLs in the browser
 *   - custom/app/policies/custom/*        rejects the writes at the API
 * The Ruby side is a separate list by necessity (different language), and
 * config/initializers/zz_ochat_policy_prepends.rb is what keeps it in step.
 *
 * ROLES
 * Chatwoot already ships the three levels, so nothing new was added to the
 * database:
 *
 *   agent          account_user.role = 0        -> permission 'agent'
 *   administrator  account_user.role = 1        -> permission 'administrator'
 *   super admin    users.type = 'SuperAdmin'    -> permission 'super_admin'
 *
 * The first two come from AccountUser#permissions upstream; the third is added by
 * custom/app/models/custom/account_user.rb.
 *
 * `menu` holds the `name` upstream gives each sidebar node. `routes` holds route
 * name patterns, where a leading or trailing '*' is a wildcard. Both are matched
 * against the real tree by specs, because an upstream rename would otherwise
 * expose a section in silence -- the same failure mode the purged Tailwind classes
 * had.
 *
 * NOT LISTED HERE, ON PURPOSE: Teams, Canned Responses, Macros and Labels.
 * Administrators are meant to manage those themselves.
 */
export const RESERVED_SECTIONS = [
  {
    key: 'captain',
    why: 'AI assistant - not part of the Ochat offering',
    menu: ['Captain'],
    // Covers captain_assistants_*, captain_tools_index and captain_settings_index.
    routes: ['captain_*'],
  },
  {
    key: 'campaigns',
    why: 'Superseded by Ochat Marketing (broadcasts + social posts)',
    menu: ['Campaigns'],
    routes: ['campaigns_*'],
  },
  {
    key: 'portals',
    why: 'Help Center',
    menu: ['Portals'],
    routes: ['portals_*'],
  },
  {
    key: 'reports',
    why: 'Replaced for everyone else by the embedded DashboardAnalytics view',
    menu: ['Reports'],
    // Upstream names these by subject, not by prefix: account_overview_reports,
    // csat_reports, agent_reports_index... hence the suffix wildcards.
    routes: ['*_reports', '*_reports_*'],
  },
  {
    key: 'inboxes',
    why: 'Inboxes - channel setup is operator work',
    menu: ['Settings Inboxes'],
    routes: ['settings_inbox_*', 'settings_inboxes_*'],
  },
  {
    key: 'whatsapp_templates',
    why: 'Plantillas de WhatsApp - configuracion del canal, no del negocio',
    menu: ['Settings Templates'],
    // Llego con 4.17.1 y entro sin reservar: es una seccion nueva de upstream,
    // no un renombre, asi que ninguna de las guardas existentes la cubria. Va con
    // Bandejas porque es lo mismo visto de otro lado -- la pantalla lista las
    // bandejas de WhatsApp y sincroniza sus plantillas contra Meta.
    //
    // Route guard only. La unica escritura de la pantalla es
    // `inboxes/syncTemplates`, que pasa por InboxPolicy#sync_templates? y ESA ya
    // esta reservada en custom/app/policies/custom/inbox_policy.rb, dentro de la
    // seccion `inboxes`. El resto de lo que hace es leer bandejas y plantillas, y
    // las lecturas quedan abiertas por diseno.
    routes: ['settings_templates'],
  },
  {
    key: 'automation',
    why: 'Automation rules',
    menu: ['Settings Automation'],
    routes: ['automation_list'],
  },
  {
    key: 'agent_bots',
    why: 'Agent bots',
    menu: ['Settings Agent Bots'],
    routes: ['agent_bots'],
  },
  {
    key: 'integrations',
    why: 'Integrations',
    menu: ['Settings Integrations'],
    routes: ['settings_applications*', 'settings_integrations_*'],
  },
  {
    key: 'audit_logs',
    why: 'Audit logs',
    menu: ['Settings Audit Logs'],
    routes: ['auditlogs_list'],
  },
  {
    key: 'custom_roles',
    why: 'Custom roles',
    menu: ['Settings Custom Roles'],
    routes: ['custom_roles_list'],
  },
  {
    key: 'sla',
    why: 'SLA policies',
    menu: ['Settings Sla'],
    routes: ['sla_list', 'sla_wrapper'],
  },
  {
    key: 'conversation_workflow',
    why: 'Conversation workflow',
    menu: ['Conversation Workflow'],
    // Route guard only: this one persists in account.settings and goes through
    // AccountPolicy#update?, so reserving it server-side would block every
    // account edit. See custom/app/policies/custom/super_admin_only.rb.
    routes: ['conversation_workflow_index'],
  },
  {
    key: 'unattended',
    why: 'Desatendido: la definicion de upstream no describe una cuenta con IA',
    menu: ['Unattended'],
    // Route guard only, y a proposito: `unattended` es un filtro de lectura sobre
    // conversaciones que el administrador ya puede ver desde Todas. No hay nada
    // que reservar en la API; lo que se saca es una pestana que enganna.
    //
    // QUE MIDE HOY
    // Conversation#unattended es `first_reply_created_at IS NULL OR waiting_since
    // IS NOT NULL`, sobre las abiertas. La primera mitad marca "ningun humano
    // contesto nunca" y la segunda "hay un entrante sin responder".
    //
    // POR QUE NO DESCRIBE UNA CUENTA CON IA
    // El agente de n8n publica con token de un User, asi que sus respuestas
    // cuentan como humanas: setean first_reply_created_at y limpian
    // waiting_since. La pestana termina mostrando los segundos que tarda la IA en
    // contestar, y se vacia sola. Y el caso que si importa --derivado a un asesor
    // y nadie contesto-- no aparece, porque la derivacion ocurre justo despues de
    // que la IA respondio, con waiting_since ya en nulo; recien entra si el
    // cliente vuelve a escribir.
    //
    // QUE HARIA FALTA PARA QUE SIRVA
    // Redefinir el scope en el overlay como "abiertas con waiting_since mas viejo
    // que X minutos". Con la automatizacion pausada tras la derivacion, eso es
    // exactamente "derivado y nadie contesto", y de paso saca del medio la
    // latencia de la IA. Es una linea, pero cambia tambien la metrica
    // `unattended` de Reportes (report_builder.rb y live_reports_controller.rb
    // usan el mismo scope) y hay que elegir el umbral. Se deja para despues de
    // validar la actualizacion.
    routes: ['conversation_unattended', 'conversation_through_unattended'],
  },
  {
    key: 'custom_attributes',
    why: 'Custom attribute definitions - schema, not day-to-day data',
    menu: ['Settings Custom Attributes'],
    routes: ['attributes_list'],
  },
  {
    key: 'security',
    why: 'Security / SAML',
    menu: ['Settings Security'],
    routes: ['security_settings_index'],
  },
];

/** Every reserved sidebar node name, flattened. */
export const RESERVED_MENU_NAMES = RESERVED_SECTIONS.flatMap(
  section => section.menu
);

/** Every reserved route name pattern, flattened. */
export const RESERVED_ROUTE_PATTERNS = RESERVED_SECTIONS.flatMap(
  section => section.routes
);

/**
 * Matches a route name against one pattern. '*' is allowed at either end or both;
 * anywhere else it is treated literally, which is fine because no upstream route
 * name contains one.
 *
 * @param {string} name route name
 * @param {string} pattern e.g. 'captain_*', '*_reports', 'agent_bots'
 * @returns {boolean}
 */
export const matchesRoutePattern = (name, pattern) => {
  if (typeof name !== 'string') return false;

  const startsAny = pattern.startsWith('*');
  const endsAny = pattern.endsWith('*');
  const body = pattern.slice(startsAny ? 1 : 0, endsAny ? -1 : undefined);

  if (startsAny && endsAny) return name.includes(body);
  if (startsAny) return name.endsWith(body);
  if (endsAny) return name.startsWith(body);
  return name === pattern;
};

/**
 * @param {string} name route name
 * @returns {boolean} whether this route belongs to a reserved section
 */
export const isReservedRoute = name =>
  RESERVED_ROUTE_PATTERNS.some(pattern => matchesRoutePattern(name, pattern));

export default RESERVED_SECTIONS;
